/**
 * PROM submission <-> FHIR `QuestionnaireResponse` + `Observation` mapper
 * (PROD-642 Sprint 2.2).
 *
 * The Pathway Studio dispatches Patient-Reported Outcome Measures (PROMs)
 * such as MSQ (Menopause Specific Questionnaire) baseline through the
 * JITAI surface. Submission carries a structured payload with item-level
 * answers + a derived score. The canonical FHIR shape is:
 *   - `QuestionnaireResponse` for the per-item answers, and
 *   - `Observation` for the derived score (LOINC code), `derivedFrom`
 *     pointing back at the QuestionnaireResponse.
 *
 * The mapper is symmetric: `promToFhir` produces both resources, and
 * `fhirToProm` reconstructs the submission given the pair. The schema
 * is intentionally minimal so the prototype can extend it per PROM type
 * without churning the wire contract.
 *
 * HC5: zero em / en dashes anywhere.
 */
import type {
  Observation,
  QuestionnaireResponse,
  QuestionnaireResponseItem,
  QuestionnaireResponseItemAnswer,
} from '../fhir/types';
import { TENANT_EXTENSION_URL } from '../fhir/types';

/** Single PROM answer value union. */
export type PromAnswerValue = string | number | boolean;

export interface PromItem {
  /** Stable item id, e.g. `msq-q1`. */
  linkId: string;
  /** Display text for the item. */
  text?: string;
  /** Caller-supplied answer. */
  value: PromAnswerValue;
}

export interface PromScore {
  /** LOINC code for the derived metric (e.g. `82667-7` for MSQ total). */
  loincCode: string;
  /** Display label, e.g. `MSQ baseline total score`. */
  display: string;
  /** Numeric score value. */
  value: number;
  /** Optional unit of measure. */
  unit?: string;
}

export interface PromSubmission {
  /**
   * Stable identifier for this submission. Doubles as the
   * `QuestionnaireResponse.identifier` value so the round-trip is
   * deterministic.
   */
  submissionId: string;
  questionnaireUrl: string;
  patientReference: string;
  practitionerReference?: string;
  tenantId: string;
  authored: string;
  items: PromItem[];
  score: PromScore;
}

const LOINC_SYSTEM = 'http://loinc.org';

function answerFor(value: PromAnswerValue): QuestionnaireResponseItemAnswer {
  if (typeof value === 'string') return { valueString: value };
  if (typeof value === 'boolean') return { valueBoolean: value };
  if (Number.isInteger(value)) return { valueInteger: value };
  return { valueDecimal: value };
}

function readAnswer(
  answer: QuestionnaireResponseItemAnswer,
): PromAnswerValue {
  if (answer.valueString !== undefined) return answer.valueString;
  if (answer.valueBoolean !== undefined) return answer.valueBoolean;
  if (answer.valueInteger !== undefined) return answer.valueInteger;
  if (answer.valueDecimal !== undefined) return answer.valueDecimal;
  throw new Error('QuestionnaireResponse answer missing recognised value field');
}

export interface PromFhirPair {
  questionnaireResponse: QuestionnaireResponse;
  observation: Observation;
}

/** Convert a PROM submission to the FHIR resource pair. */
export function promToFhir(submission: PromSubmission): PromFhirPair {
  if (submission.items.length === 0) {
    throw new Error(
      `PROM submission "${submission.submissionId}" has zero items`,
    );
  }
  const items: QuestionnaireResponseItem[] = submission.items.map((item) => ({
    linkId: item.linkId,
    text: item.text,
    answer: [answerFor(item.value)],
  }));
  const qr: QuestionnaireResponse = {
    resourceType: 'QuestionnaireResponse',
    id: `qr-${submission.submissionId}`,
    identifier: { value: submission.submissionId },
    questionnaire: submission.questionnaireUrl,
    status: 'completed',
    subject: { reference: submission.patientReference },
    authored: submission.authored,
    author: submission.practitionerReference
      ? { reference: submission.practitionerReference }
      : undefined,
    item: items,
    extension: [
      { url: TENANT_EXTENSION_URL, valueString: submission.tenantId },
    ],
  };

  const observation: Observation = {
    resourceType: 'Observation',
    id: `obs-${submission.submissionId}`,
    status: 'final',
    code: {
      coding: [
        {
          system: LOINC_SYSTEM,
          code: submission.score.loincCode,
          display: submission.score.display,
        },
      ],
      text: submission.score.display,
    },
    subject: { reference: submission.patientReference },
    effectiveDateTime: submission.authored,
    valueQuantity: {
      value: submission.score.value,
      unit: submission.score.unit,
      system: submission.score.unit ? 'http://unitsofmeasure.org' : undefined,
      code: submission.score.unit,
    },
    derivedFrom: [{ reference: `QuestionnaireResponse/qr-${submission.submissionId}` }],
    extension: [
      { url: TENANT_EXTENSION_URL, valueString: submission.tenantId },
    ],
  };
  return { questionnaireResponse: qr, observation };
}

/** Inverse mapping: parse the QR + Observation pair back into a submission. */
export function fhirToProm(pair: PromFhirPair): PromSubmission {
  const { questionnaireResponse: qr, observation } = pair;
  if (qr.resourceType !== 'QuestionnaireResponse') {
    throw new Error('fhirToProm: expected QuestionnaireResponse');
  }
  if (observation.resourceType !== 'Observation') {
    throw new Error('fhirToProm: expected Observation');
  }
  const submissionId = qr.identifier?.value;
  if (!submissionId) {
    throw new Error('QuestionnaireResponse missing identifier.value');
  }
  const tenantExt = qr.extension?.find((e) => e.url === TENANT_EXTENSION_URL);
  const tenantId = tenantExt?.valueString;
  if (!tenantId) {
    throw new Error('QuestionnaireResponse missing tenant-id extension');
  }
  const questionnaireUrl = qr.questionnaire ?? '';
  if (!questionnaireUrl) {
    throw new Error('QuestionnaireResponse missing questionnaire url');
  }
  const patientReference = qr.subject?.reference;
  if (!patientReference) {
    throw new Error('QuestionnaireResponse missing subject.reference');
  }
  const items: PromItem[] = (qr.item ?? []).map((item) => {
    const ans = item.answer?.[0];
    if (!ans) {
      throw new Error(
        `QuestionnaireResponse.item "${item.linkId}" missing answer`,
      );
    }
    return {
      linkId: item.linkId,
      text: item.text,
      value: readAnswer(ans),
    };
  });
  const coding = observation.code.coding?.find((c) => c.system === LOINC_SYSTEM);
  if (!coding?.code) {
    throw new Error('Observation.code missing LOINC coding');
  }
  const valueQuantity = observation.valueQuantity;
  if (!valueQuantity || typeof valueQuantity.value !== 'number') {
    throw new Error('Observation.valueQuantity missing numeric value');
  }
  const score: PromScore = {
    loincCode: coding.code,
    display: coding.display ?? observation.code.text ?? '',
    value: valueQuantity.value,
    unit: valueQuantity.unit,
  };
  return {
    submissionId,
    questionnaireUrl,
    patientReference,
    practitionerReference: qr.author?.reference,
    tenantId,
    authored: qr.authored ?? observation.effectiveDateTime ?? '',
    items,
    score,
  };
}
